import React, { useEffect, useState } from 'react'
import { Card, CardContent, Container, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AppleIcon from '@material-ui/icons/Apple'
import AndroidIcon from '@material-ui/icons/Android'
import { useDispatch } from 'react-redux'

import LoadingOverlay from '../../components/Misc/LoadingOverlay'
import StyledSwitchWithLabel from '../../components/Common/StyledSwitchWithLabel'
import StyledTextField from '../../components/Common/StyledTextField'
import StandardConfirmationDialog from '../../components/Common/Dialogs/StandardConfirmation'
import { MainButton } from '../../components/Button/MainButton'
import { showError, showSuccess } from '../../actions/misc/misc.actions'
import { api } from '../../services/api'
import { MainTitle } from '../../components'
import { gray } from '../../utils/colors'

const MAX_DESCRIPTION = 120
const VERSION_PATTERN = /^\d+\.\d+\.\d+$/

// iOS and Android are configured independently — one section per platform.
const PLATFORM_SECTIONS = [
	{ platform: 'ios', title: 'Apple', Icon: AppleIcon },
	{ platform: 'android', title: 'Android', Icon: AndroidIcon }
]

// Numeric compare of two dotted version strings. Returns -1 / 0 / 1.
const compareVersions = (a, b) => {
	const pa = String(a)
		.split('.')
		.map((n) => parseInt(n, 10) || 0)
	const pb = String(b)
		.split('.')
		.map((n) => parseInt(n, 10) || 0)
	for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
		const diff = (pa[i] || 0) - (pb[i] || 0)
		if (diff !== 0) return diff < 0 ? -1 : 1
	}
	return 0
}

// Only these fields are editable from this surface; everything else on the app_update
// document (store_url, force_update, update_history) is managed elsewhere.
const toForm = (doc = {}) => ({
	alert_users: !!doc.alert_users,
	version_number: doc.version_number || '',
	version_description: doc.version_description || ''
})

const useStyles = makeStyles((theme) => ({
	root: {
		paddingTop: '24px',
		paddingLeft: '25px',
		paddingRight: '20px',
		paddingBottom: '75px'
	},
	subtitle: {
		color: theme.palette.text.secondary,
		marginBottom: 24,
		maxWidth: 720
	},
	card: {
		maxWidth: 720,
		marginBottom: 24
	},
	cardContent: {
		padding: 24,
		'&:last-child': {
			paddingBottom: 24
		}
	},
	sectionHeading: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		fontWeight: 700,
		marginBottom: 16,
		position: 'relative',
		'& .MuiSvgIcon-root': {
			fill: theme.palette.text.primary,
			fontSize: 16
		}
	},
	iconBadge: {
		width: 30,
		height: 30,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		borderRadius: '50%',
		backgroundColor: gray[300]
	},
	block: {
		marginBottom: 24,
		flex: 1
	},
	actions: {
		display: 'flex',
		flexDirection: 'row',
		gap: 8
	}
}))

const ACTION_BUTTON_STYLE = { height: 38 }

const PlatformSection = ({ platform, title, Icon, initialDoc, classes }) => {
	const dispatch = useDispatch()

	const [original, setOriginal] = useState(toForm(initialDoc))
	const [form, setForm] = useState(toForm(initialDoc))
	const [saving, setSaving] = useState(false)
	const [togglingAlert, setTogglingAlert] = useState(false)
	// The version the alert was last shown at (read-only, from the server).
	const [lastAlertedVersion, setLastAlertedVersion] = useState(initialDoc.last_alerted_version || '')
	const [confirmOpen, setConfirmOpen] = useState(false)

	const applyDoc = (doc) => {
		setOriginal(toForm(doc))
		setForm(toForm(doc))
		setLastAlertedVersion(doc.last_alerted_version || '')
	}

	const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))

	// The alert toggle saves on its own (see performToggle), so "dirty" only tracks the
	// version + message fields that the Save button owns.
	const dirty =
		form.version_number !== original.version_number ||
		form.version_description !== original.version_description
	const versionValid = VERSION_PATTERN.test(form.version_number)
	// version_number is locked while the alert is already on (backend guardrail).
	const versionLocked = original.alert_users
	// Version can only move forward — never go below what's stored (backend guardrail).
	const versionWentBackward = versionValid && compareVersions(form.version_number, original.version_number) < 0
	const canSave = dirty && !saving && versionValid && !versionWentBackward

	// True when turning the alert on at a version it was already shown at (not bumped).
	const reEnablingSameVersion = !!lastAlertedVersion && form.version_number === lastAlertedVersion

	// The alert toggle is its own immediate save — flip optimistically, PATCH just
	// alert_users, and revert if it fails.
	const performToggle = async (checked) => {
		setTogglingAlert(true)
		setForm((prev) => ({ ...prev, alert_users: checked }))
		try {
			const res = await api.patch('/app-update', { platform, alert_users: checked })
			const doc = res.data?.data ?? res.data
			setOriginal(toForm(doc))
			setForm((prev) => ({ ...prev, alert_users: doc.alert_users }))
			setLastAlertedVersion(doc.last_alerted_version || '')
		} catch (e) {
			setForm((prev) => ({ ...prev, alert_users: !checked }))
			const errors = e.response?.data?.errors
			dispatch(showError(errors && errors.length ? errors[0] : 'Failed to update the alert'))
		} finally {
			setTogglingAlert(false)
		}
	}

	// Turning the alert ON at an un-bumped version warns first; everything else toggles directly.
	const handleToggle = (checked) => {
		if (checked && reEnablingSameVersion) {
			setConfirmOpen(true)
			return
		}
		performToggle(checked)
	}

	const confirmReEnable = () => {
		setConfirmOpen(false)
		performToggle(true)
	}

	const save = async () => {
		setSaving(true)
		try {
			// version_number can only change while the alert is off; omit it otherwise.
			const payload = { platform, version_description: form.version_description }
			if (!versionLocked) payload.version_number = form.version_number

			const res = await api.patch('/app-update', payload)
			const doc = res.data?.data ?? res.data
			applyDoc(doc)
			dispatch(showSuccess(`${title} app update saved`))
		} catch (e) {
			const errors = e.response?.data?.errors
			dispatch(showError(errors && errors.length ? errors[0] : 'Failed to save app update'))
		} finally {
			setSaving(false)
		}
	}

	const cancel = () => setForm({ ...original })

	return (
		<Card className={classes.card}>
			<CardContent className={classes.cardContent}>
				<Typography variant="body" className={classes.sectionHeading}>
					{Icon ? (
						<span className={classes.iconBadge}>
							<Icon fontSize="small" />
						</span>
					) : null}
					{title}
				</Typography>

				<div className={classes.block}>
					<StyledSwitchWithLabel
						label="Show app update"
						checked={form.alert_users}
						disabled={togglingAlert || saving}
						onChecked={(e) => handleToggle(e.target.checked)}
					/>
				</div>

				<div className={classes.block}>
					<StyledTextField
						label="App Version"
						variant="filled"
						value={form.version_number}
						disabled={versionLocked}
						error={(!!form.version_number && !versionValid) || versionWentBackward}
						helperText={
							versionLocked
								? 'Turn the alert off to change the version.'
								: versionWentBackward
								? `Version can only move forward (at least ${original.version_number}).`
								: 'Semantic version, e.g. 13.1.0'
						}
						onChange={(e) => update('version_number', e.target.value)}
					/>
				</div>

				<div className={classes.block}>
					<StyledTextField
						label="Update Message"
						variant="filled"
						multiline
						charcounter
						value={form.version_description}
						inputProps={{ maxLength: MAX_DESCRIPTION }}
						onChange={(e) => update('version_description', e.target.value)}
					/>
				</div>

				<div className={classes.actions}>
					<MainButton
						label="Save"
						variant="contained"
						color="secondary"
						disabled={!canSave}
						onClick={save}
						style={ACTION_BUTTON_STYLE}
					/>
					<MainButton
						label="Cancel"
						variant="outlined"
						color="secondary"
						disabled={!dirty || saving}
						onClick={cancel}
						style={ACTION_BUTTON_STYLE}
					/>
				</div>
			</CardContent>

			<StandardConfirmationDialog
				open={confirmOpen}
				title="Turn app update on?"
				description={`The ${title} app version (${form.version_number}) hasn't changed since this alert was last shown, so users who already dismissed it won't see it again. Turn it on anyway?`}
				confirmButtonText="Turn on"
				onConfirm={confirmReEnable}
				onCancel={() => setConfirmOpen(false)}
			/>
			{saving ? <LoadingOverlay /> : null}
		</Card>
	)
}

const AppUpdate = () => {
	const classes = useStyles()
	const dispatch = useDispatch()

	// Both platform records, keyed by platform: { ios, android }.
	const [data, setData] = useState(null)
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		load()
	}, [])

	const load = async () => {
		setLoading(true)
		try {
			const res = await api.get('/app-update')
			setData(res.data?.data ?? res.data)
		} catch (e) {
			dispatch(showError('Failed to load app update settings'))
		} finally {
			setLoading(false)
		}
	}

	if (!data) {
		return <div>{loading ? <LoadingOverlay /> : null}</div>
	}

	return (
		<div>
			<Container className={classes.root} maxWidth="auto">
				<MainTitle title="App Update" />

				<Typography variant="body2" className={classes.subtitle}>
					When turned on this alert will appear in the app for all users, on that platform, whose version
					number is lower than the app version set below. Users can dismiss the alert 3 times and it
					won't appear again until a new app version is set. iOS and Android are configured separately.
				</Typography>

				{PLATFORM_SECTIONS.map(({ platform, title, Icon }) => (
					<PlatformSection
						key={platform}
						platform={platform}
						title={title}
						Icon={Icon}
						initialDoc={data[platform] || {}}
						classes={classes}
					/>
				))}
			</Container>
			{loading ? <LoadingOverlay /> : null}
		</div>
	)
}

export default AppUpdate
